import { Recipe, Ingredient } from '@/types/recipe';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Edit, Save, UtensilsCrossed, X, Plus, Trash2 } from 'lucide-react';
import React from 'react';

interface RecipeIngredientsProps {
  ingredients: Ingredient[];
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onIngredientChange: (ingredients: Ingredient[]) => void;
}

export const RecipeIngredients = ({
  ingredients,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onIngredientChange,
}: RecipeIngredientsProps) => {
  const [newIngredient, setNewIngredient] = React.useState<Ingredient>({
    id: 0,
    name: '',
    amount: 0,
    metric: { id: 0, name: '' },
    optional: false,
    cost: 0
  });

  const handleAddIngredient = () => {
    if (newIngredient.name && newIngredient.amount > 0) {
      onIngredientChange([...ingredients, { ...newIngredient, id: Date.now() }]);
      setNewIngredient({
        id: 0,
        name: '',
        amount: 0,
        metric: { id: 0, name: '' },
        optional: false,
        cost: 0
      });
    }
  };

  const handleRemoveIngredient = (id: number) => {
    onIngredientChange(ingredients.filter(ing => ing.id !== id));
  };

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="bg-secondary/30 border-b">
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <UtensilsCrossed className="h-5 w-5 text-primary" />
            Ingredients
          </CardTitle>
          {!isEditing ? (
            <Button onClick={onEdit} variant="outline" size="sm" className="bg-card text-foreground">
              <Edit className="h-4 w-4 mr-2" />
              Edit Ingredients
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button onClick={onSave} size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button onClick={onCancel} variant="outline" size="sm">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-6 bg-card text-foreground">
        <div className="space-y-4">
          {!isEditing ? (
            <Table>
              <TableHeader className="bg-muted">
                <TableRow>
                  <TableHead className="text-foreground">Name</TableHead>
                  <TableHead className="text-foreground">Quantity</TableHead>
                  <TableHead className="text-foreground">Unit</TableHead>
                  <TableHead className="text-foreground">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ingredients.map((ingredient) => (
                  <TableRow key={ingredient.id} className="hover:bg-muted/50 border-border">
                    <TableCell className="text-foreground">{ingredient.name}</TableCell>
                    <TableCell className="text-foreground">{ingredient.amount}</TableCell>
                    <TableCell className="text-foreground">{ingredient.metric ?? ""}</TableCell>
                    <TableCell className="text-foreground">${ingredient.cost}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="text-foreground">Name</TableHead>
                    <TableHead className="text-foreground">Quantity</TableHead>
                    <TableHead className="text-foreground">Unit</TableHead>
                    <TableHead className="text-foreground">Cost</TableHead>
                    <TableHead className="text-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ingredients.map((ingredient) => (
                    <TableRow key={ingredient.id} className="hover:bg-muted/50 border-border">
                      <TableCell>
                        <Input
                          value={ingredient.name}
                          className="border-border bg-background text-foreground"
                          onChange={(e) => {
                            const updatedIngredients = ingredients.map(ing =>
                              ing.id === ingredient.id ? { ...ing, name: e.target.value } : ing
                            );
                            onIngredientChange(updatedIngredients);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className="border-border bg-background text-foreground"
                          value={ingredient.amount}
                          onChange={(e) => {
                            const updatedIngredients = ingredients.map(ing =>
                              ing.id === ingredient.id ? { ...ing, amount: parseFloat(e.target.value) } : ing
                            );
                            onIngredientChange(updatedIngredients);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          className="border-border bg-background text-foreground"
                          value={ingredient.metric.name}
                          onChange={(e) => {
                            const updatedIngredients = ingredients.map(ing =>
                              ing.id === ingredient.id ? { ...ing, metric: { ...ing.metric, name: e.target.value } } : ing
                            );
                            onIngredientChange(updatedIngredients);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className="border-border bg-background text-foreground"
                          value={ingredient.cost}
                          onChange={(e) => {
                            const updatedIngredients = ingredients.map(ing =>
                              ing.id === ingredient.id ? { ...ing, cost: parseFloat(e.target.value) } : ing
                            );
                            onIngredientChange(updatedIngredients);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveIngredient(ingredient.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Input
                    placeholder="Ingredient name"
                    className="border-border bg-background text-foreground"
                    value={newIngredient.name}
                    onChange={(e) => setNewIngredient({ ...newIngredient, name: e.target.value })}
                  />
                </div>
                <div className="w-24 space-y-2">
                  <Input
                    type="number"
                    placeholder="Amount"
                    className="border-border bg-background text-foreground"
                    value={newIngredient.amount || ''}
                    onChange={(e) => setNewIngredient({ ...newIngredient, amount: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="w-24 space-y-2">
                  <Input
                    placeholder="Unit"
                    className="border-border bg-background text-foreground"
                    value={newIngredient.metric.name}
                    onChange={(e) => setNewIngredient({
                      ...newIngredient,
                      metric: { ...newIngredient.metric, name: e.target.value }
                    })}
                  />
                </div>
                <div className="w-24 space-y-2">
                  <Input
                    type="number"
                    placeholder="Cost"
                    className="border-border bg-background text-foreground"
                    value={newIngredient.cost || ''}
                    onChange={(e) => setNewIngredient({ ...newIngredient, cost: parseFloat(e.target.value) })}
                  />
                </div>
                <Button onClick={handleAddIngredient} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};